import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { APIProvider, Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
// The shared list — this file used to keep its own copy, which is exactly
// how Arjan came out grey instead of JVC's brown and Al Barsha South was
// left out again (2026-08-30).
import { colorOf as colorFor } from '../../../constants/areas';
import ZoneCheck from './ZoneCheck';

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
const DUBAI = { lat: 25.14, lng: 55.23 };

/**
 * Where we work, and what we turned away.
 *
 * The shapes are the PUBLISHED community boundaries, cached from OpenStreetMap
 * — the actual areas, not an outline drawn round our own buildings. Where no
 * boundary exists the map falls back to the footprint of our properties and
 * says so in the legend, so a stand-in is never read as a border. The palette
 * is the scheduling board's, so an area is the same colour here as on the day
 * sheet.
 */

export default function LeadsCoverageMap({ onClose }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [showMissed, setShowMissed] = useState(false);
    const [hover, setHover] = useState(null);
    const [checked, setChecked] = useState(null);

    useEffect(() => {
        axios.get('/api/leads/coverage')
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.error || 'Could not load the coverage map.'));
    }, []);

    if (!GMAPS_KEY) {
        return (
            <div className="lds__panel" style={{ marginBottom: 16 }}>
                <div className="lds__err">
                    The map cannot load — REACT_APP_GOOGLE_MAPS_KEY is not set in this build.
                </div>
            </div>
        );
    }

    return (
        <div className="lds__panel lds__coverage" style={{ marginBottom: 16 }}>
            <div className="lds__panelHead">
                <div>
                    <h4 className="lds__panelTitle">Coverage area</h4>
                    <p className="lds__panelNote">
                        The real community boundaries, with the number of active properties
                        we clean inside each.
                    </p>
                </div>
                <div className="lds__pager">
                    <label className="lds__toggle">
                        <input type="checkbox" checked={showMissed}
                            onChange={(e) => setShowMissed(e.target.checked)} />
                        Show missed units
                    </label>
                    <button className="lds__btn" onClick={onClose}>Hide map</button>
                </div>
            </div>

            {error && <div className="lds__err">{error}</div>}

            {showMissed && data && data.unpinned > 0 && (
                <div className="lds__note">
                    {data.missed.length === 0
                        ? `None of the ${data.unpinned} leads turned away over the area has a pin yet — `
                        : `${data.unpinned} more were turned away over the area but never pinned — `}
                    they were logged before the map existed. Open one and pin it and it appears here.
                </div>
            )}

            <ZoneCheck onPick={setChecked} />

            <div className="lds__coverageMap">
                {hover && (
                    <div className="lds__mapTip" style={{ left: hover.x, top: hover.y }}>
                        <strong>{hover.area}</strong>
                        <span>
                            {hover.properties} active {hover.properties === 1 ? 'property' : 'properties'}
                            {hover.shape !== 'boundary' ? ' · outline from our own units' : ''}
                        </span>
                    </div>
                )}
                <APIProvider apiKey={GMAPS_KEY} libraries={['places']}>
                    <Map
                        mapId={MAP_ID}
                        defaultCenter={DUBAI}
                        defaultZoom={10}
                        disableDefaultUI
                        zoomControl
                        gestureHandling="greedy"
                    >
                        <Shapes areas={data?.areas || []} onHover={setHover} />
                        {checked && (
                            <AdvancedMarker position={{ lat: checked.lat, lng: checked.lng }}>
                                <span className="lds__checkFlag">
                                    <span className="lds__checkFlagLabel">{checked.label}</span>
                                    <span className="lds__checkFlagStem" />
                                    <span className="lds__checkFlagDot" />
                                </span>
                            </AdvancedMarker>
                        )}
                        {showMissed && (data?.missed || []).map((m) => (
                            <AdvancedMarker key={m.id} position={{ lat: m.lat, lng: m.lng }}
                                title={`${m.place_name || m.area || 'Turned away'} — ${m.name || 'no name'}`}>
                                <span className="lds__missedPin" />
                            </AdvancedMarker>
                        ))}
                    </Map>
                </APIProvider>
            </div>

            <div className="lds__legend">
                {(data?.areas || []).map((a) => (
                    <span className="lds__legendItem" key={a.area}>
                        <span className="lds__legendDot" style={{ background: colorFor(a.area) }} />
                        {a.area} <strong>{a.properties}</strong>
                    </span>
                ))}
                {showMissed && (
                    <span className="lds__legendItem">
                        <span className="lds__legendDot lds__legendDot--missed" />
                        Turned away <strong>{data?.missed?.length ?? 0}</strong>
                    </span>
                )}
            </div>
        </div>
    );
}

/**
 * The area shapes, drawn by hand.
 *
 * @vis.gl/react-google-maps ships neither Polygon nor Circle, so the overlays
 * are created against the map instance and torn down with it — leaving them
 * attached is how a map ends up with four copies of every area after a few
 * toggles.
 *
 * A polygon wherever there are enough properties to hull; a circle only for the
 * one- and two-property areas, where there is no shape to find.
 */
function Shapes({ areas, onHover }) {
    const map = useMap();
    // Smallest drawn last, so it sits on top. The server already cuts overlaps
    // out of the larger area, but a member shape (Zabeel inside Downtown) is
    // drawn whole — and whatever is on top is what the cursor reaches.
    const list = useMemo(() => {
        const size = (a) => (a.boundary || [])
            .reduce((t, ring) => t + Math.abs(ring.reduce((s, v, i, r) => {
                const w = r[(i + 1) % r.length];
                return s + (v.lng * w.lat - w.lng * v.lat);
            }, 0) / 2), 0) || (a.radius || 0) / 1e6;
        return [...areas].sort((a, b) => size(b) - size(a));
    }, [areas]);

    useEffect(() => {
        if (!map || !window.google?.maps) return undefined;

        const drawn = list.map((a) => {
            const color = colorFor(a.area);
            const style = {
                map,
                strokeColor: color,
                strokeOpacity: 0.95,
                strokeWeight: 2,
                fillColor: color,
                fillOpacity: 0.18,
                clickable: true,
            };
            // The published boundary if there is one — Dubai Marina is a
            // MultiPolygon, so paths takes several rings — otherwise our own
            // footprint, and only a circle where even that is impossible.
            const shape = a.boundary?.length
                ? new window.google.maps.Polygon({ ...style, paths: a.boundary })
                : a.polygon && a.polygon.length >= 3
                    ? new window.google.maps.Polygon({ ...style, paths: a.polygon })
                    : new window.google.maps.Circle({ ...style, center: { lat: a.lat, lng: a.lng }, radius: a.radius });

            // The label follows the cursor, in the panel's own coordinates —
            // Google's InfoWindow drags a bubble, a tail and a close button
            // onto the map for two words. On a strip like Marina a label
            // pinned to the centre is nowhere near where you are pointing.
            shape.addListener('mousemove', (e) => {
                shape.setOptions({ fillOpacity: 0.38 });
                const box = e.domEvent?.currentTarget?.getBoundingClientRect?.()
                    || e.domEvent?.target?.closest?.('.lds__coverageMap')?.getBoundingClientRect?.();
                onHover({
                    area: a.area,
                    properties: a.properties,
                    shape: a.shape,
                    x: (e.domEvent?.clientX ?? 0) - (box?.left ?? 0) + 14,
                    y: (e.domEvent?.clientY ?? 0) - (box?.top ?? 0) + 14,
                });
            });
            shape.addListener('mouseout', () => {
                shape.setOptions({ fillOpacity: 0.18 });
                onHover(null);
            });
            return shape;
        });

        return () => drawn.forEach((sh) => {
            window.google.maps.event.clearInstanceListeners(sh);
            sh.setMap(null);
        });
    }, [map, list, onHover]);

    return null;
}
